"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRight, Clock3, Sparkles } from "lucide-react";

type BannerSale = {
  id: string;
  slug: string;
  title: string;
  summary: string;
  imageUrl: string | null;
  merchant_name: string;
  original_price: number | null;
  sale_price: number;
  ends_at: string;
};

const remaining = (endsAt: string) => {
  const milliseconds = Math.max(0, new Date(endsAt).getTime() - Date.now());
  const hours = Math.floor(milliseconds / 3_600_000);
  const minutes = Math.floor((milliseconds % 3_600_000) / 60_000);
  return `${hours}h ${minutes}min`;
};

export const FlashSaleBanners = () => {
  const [sales, setSales] = useState<BannerSale[]>([]);
  const [active, setActive] = useState(0);
  const [, tick] = useState(0);

  useEffect(() => {
    fetch("/api/flash-sales")
      .then((response) => (response.ok ? response.json() : []))
      .then(setSales)
      .catch(() => setSales([]));
  }, []);

  useEffect(() => {
    if (sales.length < 2) return;
    const timer = window.setInterval(() => setActive((current) => (current + 1) % sales.length), 6500);
    return () => window.clearInterval(timer);
  }, [sales.length]);

  useEffect(() => {
    const timer = window.setInterval(() => tick((value) => value + 1), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  if (sales.length === 0) return null;
  const sale = sales[active];

  return (
    <section className="bg-amber-300 px-4 py-3 text-slate-950">
      <div className="mx-auto flex max-w-7xl items-center gap-4">
        {sale.imageUrl && <img src={sale.imageUrl} alt="" className="hidden h-16 w-24 rounded-xl object-cover sm:block" />}
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[11px] font-extrabold tracking-[0.14em] uppercase">
            <Sparkles className="size-3.5" /> Flash sale · {sale.merchant_name}
          </p>
          <p className="truncate text-base font-extrabold sm:text-lg">{sale.title}</p>
          <p className="hidden truncate text-xs text-slate-700 md:block">{sale.summary}</p>
        </div>
        <div className="hidden shrink-0 text-right sm:block">
          <p className="font-extrabold">{Number(sale.sale_price).toFixed(2)} €</p>
          <p className="flex items-center gap-1 text-xs font-semibold"><Clock3 className="size-3" /> {remaining(sale.ends_at)}</p>
        </div>
        <Link href={`/experiencias/${sale.slug}`} className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-slate-950 px-4 text-sm font-bold text-white">
          Ver oferta <ArrowRight className="size-4" />
        </Link>
        {sales.length > 1 && (
          <div className="hidden gap-1 lg:flex">
            {sales.map((item, index) => <button key={item.id} aria-label={`Ver ${item.title}`} onClick={() => setActive(index)} className={`size-2 rounded-full ${index === active ? "bg-slate-950" : "bg-slate-950/25"}`} />)}
          </div>
        )}
      </div>
    </section>
  );
};
